import React from 'react';
import { View } from 'react-native';
import { Feather } from '@expo/vector-icons';
import * as Clipboard from 'expo-clipboard';
import Toast from 'react-native-root-toast';

import Text from '../../../components/text/Text';
import { MachineChip, Tag } from '../../../design/components';
import PressableScale from '../../../design/components/PressableScale';
import { useThemedStyles } from '../../../hooks/useThemedStyles';
import { useTheme } from '../../../theme';
import { vibrateConfirm } from '../../../utils/feedback';
import { openLocationInMaps } from '../../map/MapLink';
import StopTelemetry from './StopTelemetry';
import { createStopViewStyles } from '../StopView.styles';
import {
	getStopAddress,
	getStopLabels,
	getStopMachineDetails,
	getStopPositionName,
	getStopServiceLabels,
	getStopSiteName,
} from '../stopDetails';

const StopCopyButton = ({ text, label }) => {
	const styles = useThemedStyles(createStopViewStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const copy = async () => {
		try {
			const copied = await Clipboard.setStringAsync(text);
			if (!copied) {
				Toast.show('Unable to copy. Try again.');
				return;
			}
			vibrateConfirm();
			Toast.show(`${label} copied`);
		}
		catch {
			Toast.show('Unable to copy. Try again.');
		}
	};

	return (
		<PressableScale
			onPress={copy}
			accessibilityRole="button"
			accessibilityLabel={`Copy ${label.toLowerCase()}`}
			style={styles.copyTarget}
		>
			<View style={styles.copyButton}>
				<Feather name="copy" size={18} color={colors.accent} />
			</View>
		</PressableScale>
	);
};

/**
 * StopPlaceHeader: top of the stop screen (site, position, machine, service
 * and stop labels).
 * Props: docket, tenantSettings, showCashBag, showTub (those rows are shown below)
 */
const StopPlaceHeader = ({ docket, tenantSettings, showCashBag, showTub }) => {
	const styles = useThemedStyles(createStopViewStyles);
	const machineDetails = getStopMachineDetails(docket);
	const serviceLabels = getStopServiceLabels(docket, tenantSettings);
	// Cash bag and tub values show once: on their rows below when those rows are shown.
	const labels = getStopLabels(docket, tenantSettings, { includeStepLabels: false, includeCashBag: !showCashBag, includeTub: !showTub });
	const siteName = getStopSiteName(docket);
	const heading = siteName || getStopPositionName(docket);
	const address = getStopAddress(docket);
	const placeName = docket.machine?.place?.name;
	const coordinates = docket.machine?.coordinates?.length === 2
		? docket.machine.coordinates
		: docket.machine?.location?.coordinates;
	const { theme: { ui: { colors } } } = useTheme();

	return (
		<View style={styles.place}>
			{/* Location and place as the heading; the machine number shows once, on the chip below. */}
			<View style={styles.copyRow}>
				<Text style={styles.siteName} accessibilityRole="header">{heading}</Text>
				<StopCopyButton text={heading} label="Location name" />
			</View>
			{Boolean(siteName && placeName) && <Text style={styles.positionName}>{placeName}</Text>}
			{(Boolean(address) || coordinates?.length === 2) && (
				<View style={styles.copyRow}>
					{Boolean(address) && (
						<Text
							style={styles.address}
							accessibilityRole="link"
							onPress={() => openLocationInMaps({ addressObj: docket.machine.location.addressObj })}
						>
							{address}
						</Text>
					)}
					{coordinates?.length === 2 && (
						<PressableScale
							onPress={() => openLocationInMaps({ coordinates, name: heading })}
							accessibilityRole="button"
							accessibilityLabel="Open saved coordinates in maps"
							style={styles.copyTarget}
						>
							<View style={styles.copyButton}>
								<Feather name="map-pin" size={18} color={colors.accent} />
							</View>
						</PressableScale>
					)}
					{Boolean(address) && <StopCopyButton text={address} label="Address" />}
				</View>
			)}
			<View style={styles.machineLine}>
				<MachineChip number={docket.machine?.assetId} size="large" />
				{Boolean(machineDetails) && <Text style={styles.machineDetails}>{machineDetails}</Text>}
			</View>
			<StopTelemetry machine={docket.machine} tenantSettings={tenantSettings} />
			{(serviceLabels.length > 0 || labels.length > 0) && (
				<View style={styles.tags}>
					{serviceLabels.map(label => <Tag key={label} label={label} size="regular" />)}
					{labels.map(label => <Tag key={label.label} {...label} size="regular" />)}
				</View>
			)}
		</View>
	);
};

export default StopPlaceHeader;
